import React from "react";
import { useTheme } from "../hooks/useTheme";

interface ThemeToggleProps {
  className?: string;
  size?: "sm" | "md" | "lg";
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  className = "",
  size = "md",
}) => {
  const { theme, toggleTheme } = useTheme();

  const sizeClasses = {
    sm: "w-8 h-8 text-sm",
    md: "w-10 h-10 text-base",
    lg: "w-12 h-12 text-lg",
  };

  return (
    <>
      <style>{`
        .theme-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid var(--color-border);
          border-radius: var(--radius-full);
          background: var(--color-surface);
          color: var(--color-text-primary);
          cursor: pointer;
          transition: all var(--transition-fast);
          position: relative;
          overflow: hidden;
        }

        .theme-toggle:hover {
          border-color: var(--color-border-strong);
          background: var(--color-surface-hover);
          transform: scale(1.05);
        }

        .theme-toggle:active {
          transform: scale(0.95);
        }

        .theme-toggle-icon {
          transition: all var(--transition-fast);
          position: relative;
          z-index: 1;
        }

        .theme-toggle-icon.sun {
          opacity: ${theme === "light" ? 1 : 0};
          transform: ${
            theme === "light"
              ? "rotate(0deg) scale(1)"
              : "rotate(180deg) scale(0.5)"
          };
        }

        .theme-toggle-icon.moon {
          opacity: ${theme === "dark" ? 1 : 0};
          transform: ${
            theme === "dark"
              ? "rotate(0deg) scale(1)"
              : "rotate(-180deg) scale(0.5)"
          };
          position: absolute;
          top: 50%;
          left: 50%;
          transform-origin: center;
          margin-left: -0.5em;
          margin-top: -0.5em;
        }

        .theme-toggle::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 100%;
          height: 100%;
          background: ${
            theme === "dark"
              ? "radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)"
              : "radial-gradient(circle, rgba(251, 191, 36, 0.1) 0%, transparent 70%)"
          };
          transform: translate(-50%, -50%);
          opacity: ${theme === "dark" ? 1 : 0.7};
          transition: all var(--transition-fast);
        }

        /* Tooltip */
        .theme-toggle[title]:hover::after {
          content: attr(title);
          position: absolute;
          bottom: -2.5rem;
          left: 50%;
          transform: translateX(-50%);
          background: var(--color-surface-elevated);
          color: var(--color-text-primary);
          padding: var(--space-xs) var(--space-sm);
          border-radius: var(--radius-sm);
          font-size: var(--font-size-xs);
          white-space: nowrap;
          box-shadow: var(--shadow-md);
          z-index: 1000;
          border: 1px solid var(--color-border);
        }

        .w-8 { width: 2rem; }
        .h-8 { height: 2rem; }
        .w-10 { width: 2.5rem; }
        .h-10 { height: 2.5rem; }
        .w-12 { width: 3rem; }
        .h-12 { height: 3rem; }
        .text-sm { font-size: 0.875rem; }
        .text-base { font-size: 1rem; }
        .text-lg { font-size: 1.125rem; }
      `}</style>

      <button
        className={`theme-toggle ${sizeClasses[size]} ${className}`}
        onClick={toggleTheme}
        title={`Switch to ${theme === "light" ? "dark" : "light"} theme`}
        aria-label={`Switch to ${theme === "light" ? "dark" : "light"} theme`}
      >
        <span className="theme-toggle-icon sun">☀️</span>
        <span className="theme-toggle-icon moon">🌙</span>
      </button>
    </>
  );
};
